
import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { 
  AlertCircle, 
  ArrowRight, 
  Briefcase, 
  Calendar, 
  ChevronRight, 
  Clock, 
  Download, 
  Globe, 
  Lightbulb, 
  MapPin, 
  Menu, 
  PiggyBank,
  Route, 
  TrendingUp 
} from 'lucide-react';
import { FundingStrategy } from '@/types/gigs';

// Mock data for funding strategies
const mockStrategies: FundingStrategy[] = [
  {
    id: '1',
    name: 'Diversified Revenue Roadmap',
    description: 'A balanced approach combining multiple revenue streams to create sustainable income with manageable risk.',
    timeframe: '12 months',
    potentialRevenue: 25000,
    riskLevel: 'medium',
    implementationSteps: [
      { step: 'Launch direct fan support program', timeframe: 'Month 1-2' },
      { step: 'Establish licensing pipeline', timeframe: 'Month 2-4' },
      { step: 'Develop limited merchandise line', timeframe: 'Month 3-5' },
      { step: 'Book strategic live performances', timeframe: 'Month 4-12' },
      { step: 'Setup publishing administration', timeframe: 'Month 5-6' }
    ],
    requiredResources: [
      'Website with e-commerce capability',
      'Merchandise production partner',
      'Music licensing platform account',
      'Publication administration service'
    ],
    recommendationReason: [
      'Matches your current career stage and fan engagement metrics',
      'Aligns with your social presence and brand positioning',
      'Utilizes your existing strengths in live performance and content creation'
    ],
    opportunityType: 'balanced'
  },
  {
    id: '2',
    name: 'Performance-Focused Acceleration',
    description: 'Capitalizing on your live performance strength to rapidly build revenue and exposure through strategic bookings and tour planning.',
    timeframe: '9 months',
    potentialRevenue: 18000,
    riskLevel: 'medium',
    implementationSteps: [
      { step: 'Develop compelling live show', timeframe: 'Month 1-2' },
      { step: 'Book strategic venue ladder', timeframe: 'Month 2-3' },
      { step: 'Launch targeted booking campaign', timeframe: 'Month 3-4' },
      { step: 'Optimize merchandise for live sales', timeframe: 'Month 3-5' },
      { step: 'Execute regional tour circuits', timeframe: 'Month 5-9' }
    ],
    requiredResources: [
      'Professional press kit and EPK',
      'Live performance video content',
      'Tour-ready merchandise',
      'Booking agent or DIY booking toolkit'
    ],
    recommendationReason: [
      'Your performance metrics indicate above-average engagement',
      'Your location gives access to multiple venue markets',
      'Recent trends show increasing live music attendance in your genre'
    ],
    opportunityType: 'performance'
  },
  {
    id: '3',
    name: 'Sync Licensing Focus',
    description: 'Strategically developing and positioning your catalog for sync licensing opportunities in film, TV, commercials and games.',
    timeframe: '18 months',
    potentialRevenue: 32000,
    riskLevel: 'high',
    implementationSteps: [
      { step: 'Audit existing catalog for sync potential', timeframe: 'Month 1' },
      { step: 'Create briefs-focused compositions', timeframe: 'Month 2-6' },
      { step: 'Develop relationships with music supervisors', timeframe: 'Month 3-12' },
      { step: 'Join key licensing platforms', timeframe: 'Month 4-5' },
      { step: 'Establish sync agent relationship', timeframe: 'Month 9-12' }
    ],
    requiredResources: [
      'Production capabilities for quick-turnaround briefs',
      'Music supervisor contact database',
      'Premium licensing platform memberships',
      'Catalog metadata management system'
    ],
    recommendationReason: [
      'Your production style matches current sync trends',
      'Your instrumental arrangements have high placement potential',
      'Your genre versatility is valuable in the sync marketplace'
    ],
    contractWarnings: [
      'Beware of exclusive representation with low placement guarantees',
      'Avoid long-term exclusive platform commitments'
    ],
    opportunityType: 'licensing'
  },
  {
    id: '4',
    name: 'Digital Growth & Monetization',
    description: 'Building and monetizing a strong digital presence through content strategy, platform optimization and direct fan engagement.',
    timeframe: '12 months',
    potentialRevenue: 15000,
    riskLevel: 'low',
    implementationSteps: [
      { step: 'Optimize streaming profiles', timeframe: 'Month 1' },
      { step: 'Develop content calendar', timeframe: 'Month 1-2' },
      { step: 'Launch direct support platform', timeframe: 'Month 2-3' },
      { step: 'Implement digital marketing funnel', timeframe: 'Month 3-6' },
      { step: 'Execute release strategy', timeframe: 'Month 6-12' }
    ],
    requiredResources: [
      'Content creation toolkit',
      'Direct-to-fan platform',
      'Digital marketing budget ($1,500-3,000)',
      'Analytics tracking system'
    ],
    recommendationReason: [
      'Your existing online engagement metrics show potential',
      'Your content creation capacity is above average',
      'Digital monetization matches your audience demographics'
    ],
    opportunityType: 'digital'
  },
  {
    id: '5',
    name: 'Grant & Funding Access',
    description: 'Systematically identifying and applying for grants, funding opportunities and artist development programs suited to your career stage.',
    timeframe: '12 months',
    potentialRevenue: 22000,
    riskLevel: 'low',
    implementationSteps: [
      { step: 'Create professional artist portfolio', timeframe: 'Month 1-2' },
      { step: 'Develop project proposals', timeframe: 'Month 2-3' },
      { step: 'Research funding calendar', timeframe: 'Month 1' },
      { step: 'Execute application schedule', timeframe: 'Month 3-12' },
      { step: 'Network with funding organizations', timeframe: 'Month 2-12' }
    ],
    requiredResources: [
      'Professional grant writing support',
      'Project budget development tools',
      'Artist statement and biography',
      'Work samples and portfolio'
    ],
    recommendationReason: [
      'Your artistic direction aligns with multiple funding priorities',
      'Your career stage matches eligibility requirements',
      'Your community involvement strengthens applications'
    ],
    opportunityType: 'grants'
  }
];

const AIFundingStrategist: React.FC = () => {
  const [selectedStrategy, setSelectedStrategy] = useState<FundingStrategy | null>(null);
  const [showAlert, setShowAlert] = useState(true);
  
  // Group strategies by risk level
  const strategyByRisk = {
    low: mockStrategies.filter(s => s.riskLevel === 'low'),
    medium: mockStrategies.filter(s => s.riskLevel === 'medium'),
    high: mockStrategies.filter(s => s.riskLevel === 'high')
  };
  
  // Helper to get background color based on risk level
  const getRiskBadgeColor = (risk: 'low' | 'medium' | 'high') => {
    switch (risk) {
      case 'low':
        return 'bg-green-100 text-green-800 hover:bg-green-200 border-green-200';
      case 'medium':
        return 'bg-amber-100 text-amber-800 hover:bg-amber-200 border-amber-200';
      case 'high':
        return 'bg-red-100 text-red-800 hover:bg-red-200 border-red-200';
    }
  };
  
  // Helper to get icon based on strategy type
  const getStrategyIcon = (type: string) => {
    switch (type) {
      case 'balanced':
        return <PiggyBank className="h-5 w-5 text-purple-600" />;
      case 'performance':
        return <Route className="h-5 w-5 text-blue-600" />;
      case 'licensing':
        return <Briefcase className="h-5 w-5 text-amber-600" />;
      case 'digital':
        return <Globe className="h-5 w-5 text-green-600" />;
      case 'grants':
        return <TrendingUp className="h-5 w-5 text-red-600" />;
      default:
        return <Lightbulb className="h-5 w-5 text-gray-600" />;
    }
  };
  
  // Format revenue for display
  const formatRevenue = (amount: number) => {
    return `$${amount.toLocaleString()}`;
  };
  
  return (
    <div className="p-6">
      {showAlert && (
        <Alert className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Your funding profile is 75% complete</AlertTitle>
          <AlertDescription>
            Complete your funding profile to receive more personalized funding strategies. 
            <Button variant="link" className="h-auto p-0 text-primary">
              Complete profile
            </Button>
          </AlertDescription>
        </Alert>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1 space-y-4">
          <Card className="border shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle>Current Funding</CardTitle>
              <CardDescription>Your active funding sources</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Live Performance</span>
                  <span>$1,850 / quarter</span>
                </div>
                <Progress value={42} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Streaming</span>
                  <span>$320 / month</span>
                </div>
                <Progress value={18} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Direct Support</span>
                  <span>$280 / month</span>
                </div>
                <Progress value={15} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Merchandise</span>
                  <span>$540 / quarter</span>
                </div>
                <Progress value={12} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Sync/Licensing</span>
                  <span>$1,200 / year</span>
                </div>
                <Progress value={8} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Session Work</span>
                  <span>$900 / quarter</span>
                </div>
                <Progress value={5} className="h-2" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle>Funding Opportunities</CardTitle>
              <CardDescription>Recent matches in your area</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                <div className="p-3 hover:bg-muted/50">
                  <div className="font-medium">Local Arts Commission Grant</div>
                  <div className="text-sm text-muted-foreground">Application due in 14 days</div>
                  <div className="flex items-center justify-between mt-1">
                    <Badge variant="outline">$5,000</Badge>
                    <Button variant="ghost" size="sm" className="h-7 text-xs">
                      <ArrowRight className="h-3.5 w-3.5 mr-1" /> Apply
                    </Button>
                  </div>
                </div>
                <div className="p-3 hover:bg-muted/50">
                  <div className="font-medium">Music Production Fund</div>
                  <div className="text-sm text-muted-foreground">Application due in 30 days</div>
                  <div className="flex items-center justify-between mt-1">
                    <Badge variant="outline">$3,500</Badge>
                    <Button variant="ghost" size="sm" className="h-7 text-xs">
                      <ArrowRight className="h-3.5 w-3.5 mr-1" /> Apply
                    </Button>
                  </div>
                </div>
                <div className="p-3 hover:bg-muted/50">
                  <div className="font-medium">Tour Development Program</div>
                  <div className="text-sm text-muted-foreground">Application due in 45 days</div>
                  <div className="flex items-center justify-between mt-1">
                    <Badge variant="outline">$7,500</Badge>
                    <Button variant="ghost" size="sm" className="h-7 text-xs">
                      <ArrowRight className="h-3.5 w-3.5 mr-1" /> Apply
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle>Revenue Insights</CardTitle>
            </CardHeader>
            <CardContent className="p-0 pt-2">
              <div className="grid grid-cols-2 gap-0 divide-x divide-y">
                <div className="p-3 flex flex-col items-center justify-center">
                  <div className="text-2xl font-bold">$1,850</div>
                  <div className="text-xs text-muted-foreground">Monthly Revenue</div>
                </div>
                <div className="p-3 flex flex-col items-center justify-center">
                  <div className="text-2xl font-bold">$22.2k</div>
                  <div className="text-xs text-muted-foreground">Annual Revenue</div>
                </div>
                <div className="p-3 flex flex-col items-center justify-center">
                  <div className="text-2xl font-bold">68%</div>
                  <div className="text-xs text-muted-foreground">Revenue Growth</div>
                </div>
                <div className="p-3 flex flex-col items-center justify-center">
                  <div className="text-2xl font-bold">5</div>
                  <div className="text-xs text-muted-foreground">Revenue Streams</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        <div className="md:col-span-2 space-y-6">
          <div>
            <h2 className="text-2xl font-bold">AI Funding Roadmap</h2>
            <p className="text-muted-foreground">
              Personalized funding strategies based on your career profile
            </p>
          </div>
          
          {!selectedStrategy ? (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-3">
                  <div className="flex items-center">
                    <Badge className={getRiskBadgeColor('low')}>Low Risk</Badge>
                    <span className="ml-2">Conservative Strategies</span>
                  </div>
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  {strategyByRisk.low.map((strategy) => (
                    <Card 
                      key={strategy.id} 
                      className="border shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => setSelectedStrategy(strategy)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="bg-muted/80 p-3 rounded-full">
                            {getStrategyIcon(strategy.opportunityType)}
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between items-start">
                              <h3 className="font-medium text-lg">{strategy.name}</h3>
                              <Badge className={getRiskBadgeColor(strategy.riskLevel)}>
                                {strategy.riskLevel.charAt(0).toUpperCase() + strategy.riskLevel.slice(1)} Risk
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground my-1">
                              {strategy.description}
                            </p>
                            <div className="flex items-center gap-6 mt-3">
                              <div className="flex items-center gap-1 text-sm">
                                <Clock className="h-4 w-4 text-muted-foreground" />
                                <span>{strategy.timeframe}</span>
                              </div>
                              <div className="flex items-center gap-1 text-sm">
                                <PiggyBank className="h-4 w-4 text-muted-foreground" />
                                <span>Up to {formatRevenue(strategy.potentialRevenue)}</span>
                              </div>
                              <div className="text-primary flex items-center ml-auto">
                                View Details <ChevronRight className="h-4 w-4" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-3">
                  <div className="flex items-center">
                    <Badge className={getRiskBadgeColor('medium')}>Medium Risk</Badge>
                    <span className="ml-2">Balanced Strategies</span>
                  </div>
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  {strategyByRisk.medium.map((strategy) => (
                    <Card 
                      key={strategy.id} 
                      className="border shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => setSelectedStrategy(strategy)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="bg-muted/80 p-3 rounded-full">
                            {getStrategyIcon(strategy.opportunityType)}
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between items-start">
                              <h3 className="font-medium text-lg">{strategy.name}</h3>
                              <Badge className={getRiskBadgeColor(strategy.riskLevel)}>
                                {strategy.riskLevel.charAt(0).toUpperCase() + strategy.riskLevel.slice(1)} Risk
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground my-1">
                              {strategy.description}
                            </p>
                            <div className="flex items-center gap-6 mt-3">
                              <div className="flex items-center gap-1 text-sm">
                                <Clock className="h-4 w-4 text-muted-foreground" />
                                <span>{strategy.timeframe}</span>
                              </div>
                              <div className="flex items-center gap-1 text-sm">
                                <PiggyBank className="h-4 w-4 text-muted-foreground" />
                                <span>Up to {formatRevenue(strategy.potentialRevenue)}</span>
                              </div>
                              <div className="text-primary flex items-center ml-auto">
                                View Details <ChevronRight className="h-4 w-4" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-3">
                  <div className="flex items-center">
                    <Badge className={getRiskBadgeColor('high')}>High Risk</Badge>
                    <span className="ml-2">Ambitious Strategies</span>
                  </div>
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  {strategyByRisk.high.map((strategy) => (
                    <Card 
                      key={strategy.id} 
                      className="border shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => setSelectedStrategy(strategy)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="bg-muted/80 p-3 rounded-full">
                            {getStrategyIcon(strategy.opportunityType)}
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between items-start">
                              <h3 className="font-medium text-lg">{strategy.name}</h3>
                              <Badge className={getRiskBadgeColor(strategy.riskLevel)}>
                                {strategy.riskLevel.charAt(0).toUpperCase() + strategy.riskLevel.slice(1)} Risk
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground my-1">
                              {strategy.description}
                            </p>
                            <div className="flex items-center gap-6 mt-3">
                              <div className="flex items-center gap-1 text-sm">
                                <Clock className="h-4 w-4 text-muted-foreground" />
                                <span>{strategy.timeframe}</span>
                              </div>
                              <div className="flex items-center gap-1 text-sm">
                                <PiggyBank className="h-4 w-4 text-muted-foreground" />
                                <span>Up to {formatRevenue(strategy.potentialRevenue)}</span>
                              </div>
                              <div className="text-primary flex items-center ml-auto">
                                View Details <ChevronRight className="h-4 w-4" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <Card className="border shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedStrategy(null)}>
                    ← Back to strategies
                  </Button>
                </div>
                <Badge className={getRiskBadgeColor(selectedStrategy.riskLevel)}>
                  {selectedStrategy.riskLevel.charAt(0).toUpperCase() + selectedStrategy.riskLevel.slice(1)} Risk
                </Badge>
              </CardHeader>
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="bg-muted/80 p-3 rounded-full">
                    {getStrategyIcon(selectedStrategy.opportunityType)}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">{selectedStrategy.name}</h2>
                    <p className="text-muted-foreground">{selectedStrategy.description}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <Card className="border shadow-sm bg-muted/50">
                    <CardContent className="p-4">
                      <div className="flex flex-col items-center text-center">
                        <Clock className="h-8 w-8 text-muted-foreground mb-2" />
                        <div className="font-medium">Timeframe</div>
                        <div className="text-2xl font-bold">{selectedStrategy.timeframe}</div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border shadow-sm bg-muted/50">
                    <CardContent className="p-4">
                      <div className="flex flex-col items-center text-center">
                        <PiggyBank className="h-8 w-8 text-muted-foreground mb-2" />
                        <div className="font-medium">Potential Revenue</div>
                        <div className="text-2xl font-bold">{formatRevenue(selectedStrategy.potentialRevenue)}</div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border shadow-sm bg-muted/50">
                    <CardContent className="p-4">
                      <div className="flex flex-col items-center text-center">
                        <Lightbulb className="h-8 w-8 text-muted-foreground mb-2" />
                        <div className="font-medium">Success Probability</div>
                        <div className="text-2xl font-bold">
                          {selectedStrategy.riskLevel === 'low' ? '75%' : selectedStrategy.riskLevel === 'medium' ? '60%' : '40%'}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Implementation Timeline</h3>
                    <div className="relative border-l-2 border-muted-foreground/20 pl-6 space-y-6 ml-2">
                      {selectedStrategy.implementationSteps.map((step, index) => (
                        <div key={index} className="relative">
                          <div className="absolute -left-8 top-0 w-4 h-4 rounded-full bg-primary border-4 border-background"></div>
                          <h4 className="font-medium">{step.step}</h4>
                          <div className="flex items-center text-sm text-muted-foreground mt-1">
                            <Calendar className="h-4 w-4 mr-1" /> {step.timeframe}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Required Resources</h3>
                    <Card className="border shadow-sm bg-muted/30">
                      <CardContent className="p-4">
                        <ul className="space-y-2">
                          {selectedStrategy.requiredResources.map((resource, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <div className="mt-1 rounded-full bg-primary h-1.5 w-1.5"></div>
                              <span>{resource}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                    
                    <h3 className="text-lg font-semibold mt-6 mb-4">Why This Strategy Fits You</h3>
                    <Card className="border shadow-sm bg-muted/30">
                      <CardContent className="p-4">
                        <ul className="space-y-2">
                          {selectedStrategy.recommendationReason.map((reason, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <div className="text-green-500 mt-0.5">✓</div>
                              <span>{reason}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                    
                    {selectedStrategy.contractWarnings && (
                      <>
                        <h3 className="text-lg font-semibold mt-6 mb-4">Contract Warning Signs</h3>
                        <Card className="border shadow-sm bg-red-50">
                          <CardContent className="p-4">
                            <ul className="space-y-2">
                              {selectedStrategy.contractWarnings.map((warning, index) => (
                                <li key={index} className="flex items-start gap-2">
                                  <div className="text-red-500 mt-0.5">⚠</div>
                                  <span>{warning}</span>
                                </li>
                              ))}
                            </ul>
                          </CardContent>
                        </Card>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 mb-6">
                  <Card className="border shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle>Revenue Projection</CardTitle>
                      <CardDescription>Estimated revenue growth over time</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="h-[250px] w-full flex items-center justify-center">
                        <div className="text-center text-muted-foreground">
                          Revenue projection chart would be displayed here
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="flex gap-3 justify-center">
                  <Button className="flex-1 md:flex-none">
                    Adopt This Strategy
                  </Button>
                  <Button variant="outline" className="flex-1 md:flex-none">
                    <Download className="h-4 w-4 mr-2" /> Download Roadmap
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIFundingStrategist;
